// File: src/pages/UpdateAccountPage.jsx

import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  LuArrowLeft,
  LuCircleCheck,
  LuMail,
  LuMailSearch,
  LuPhone,
  LuLock,
  LuUserCheck,
} from 'react-icons/lu';
import { ReactComponent as TribeLogo } from '../assets/TribeLogo.svg';
import { useToaster } from '../context/ToasterContext';

// Uber-like subtle highlight chip
const highlightStyle = {
  backgroundColor: '#f3f4f6',
  color: '#0b0b0b',
  fontWeight: 800,
  padding: '2px 8px',
  borderRadius: 6,
  display: 'inline-block',
  marginTop: 10,
};

// -------------------------------
// PAGE & CONTAINER
// -------------------------------
const pageStyle = {
  fontFamily: "'Inter', system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
  backgroundColor: '#fff',
  color: '#0b0b0b',
  minHeight: '100vh',
  margin: 0,
  position: 'relative',
};

const containerStyle = {
  maxWidth: 460,
  width: '90%',
  margin: '4.5rem auto',
  textAlign: 'center',
  zIndex: 2,
  position: 'relative',
};

// -------------------------------
// HEADER
// -------------------------------
const headerContainerStyle = {
  display: 'flex',
  flexDirection: 'column',
  position: 'sticky',
  top: 0,
  background: '#fff',
  zIndex: 100,
};

const headerLogoRowStyle = {
  display: 'flex',
  justifyContent: 'center',
  paddingTop: '0.5rem',
};

const logoSvgStyle = {
  width: 72,
  height: 'auto',
};

const headerNavRowStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  position: 'relative',
  height: 44,
  padding: '0.5rem 1rem',
};

const previousBackButtonStyle = {
  position: 'absolute',
  left: 14,
  top: '50%',
  transform: 'translateY(-50%)',
  background: 'transparent',
  width: 44,
  height: 44,
  padding: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  border: 'none',
  borderRadius: 9999,
  zIndex: 1,
  touchAction: 'manipulation',
};

const previousBackButtonFaceStyle = {
  background: '#000',
  color: '#fff',
  borderRadius: 9999,
  width: 40,
  height: 28,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  pointerEvents: 'none',
};

const pageTitlePlainStyle = {
  fontSize: '.95rem',
  fontWeight: 850,
  lineHeight: 1,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  color: '#0b0b0b',
  width: '100%',
  textAlign: 'center',
  pointerEvents: 'none',
};

const progressContainerStyle = {
  position: 'absolute',
  right: '1rem',
  top: '50%',
  transform: 'translateY(-50%)',
};

// -------------------------------
// TYPO & FORM
// -------------------------------
const headingCircleContainer = {
  width: 50,
  height: 50,
  borderRadius: 12,
  background: '#f3f4f6',
  display: 'grid',
  placeItems: 'center',
  margin: '0px auto 10px',
};

const headingStyle = {
  fontSize: '1.15rem',
  fontWeight: 900,
  color: '#0b0b0b',
  margin: '12px 0 8px',
  letterSpacing: 0.1,
};

const subTitleStyle = {
  fontSize: '0.92rem',
  color: '#374151',
  margin: '0 0 1.25rem 0',
  lineHeight: 1.5,
};

const labelStyle = {
  display: 'block',
  fontSize: '0.85rem',
  fontWeight: 700,
  color: '#111827',
  marginBottom: 6,
  textAlign: 'left',
  width: '100%',
};

function getInputStyle(isError) {
  return {
    width: '100%',
    padding: '0.65rem 0.7rem',
    fontSize: '0.95rem',
    border: `1px solid ${isError ? '#EF4444' : '#e5e7eb'}`,
    borderRadius: 8,
    boxSizing: 'border-box',
    backgroundColor: '#fff',
    color: '#0b0b0b',
    outline: 'none',
  };
}

// Primary = black; disabled = soft gray
function getPrimaryButtonStyle(isActive = true) {
  return {
    width: '100%',
    maxWidth: 300,
    padding: '0.85rem',
    fontSize: 12,
    borderRadius: 10,
    color: '#fff',
    cursor: isActive ? 'pointer' : 'not-allowed',
    fontWeight: 800,
    marginTop: '0rem',
    border: 'none',
    background: isActive ? '#000' : '#bdbdbd',
  };
}

// Secondary outline
const backBelowButtonStyle = {
  width: '100%',
  maxWidth: 300,
  margin: '0.75rem auto 0',
  fontSize: 12,
  color: '#0b0b0b',
  backgroundColor: '#fff',
  border: '1px solid #d1d5db',
  borderRadius: 10,
  cursor: 'pointer',
  fontWeight: 800,
  padding: '0.75rem',
};

// Shake animation
const shakeKeyframes = `
@keyframes shake {
  0% { transform: translateX(0); }
  20% { transform: translateX(-5px); }
  40% { transform: translateX(5px); }
  60% { transform: translateX(-5px); }
  80% { transform: translateX(5px); }
  100% { transform: translateX(0); }
}

.shake {
  animation: shake 0.3s ease;
}

.update-code-input::placeholder {
  color: #9ca3af;
  font-weight: 650;
  opacity: 1;
}
`;

// Navbar title
function getNavbarTitle() {
  return 'Update Account';
}

// Step icons
function getStepIcon(step, finalMode) {
  if (step === 2) return <LuMailSearch style={{ fontSize: '1.5rem', color: '#000' }} />;

  if (step === 3) {
    if (finalMode === 'email') return <LuUserCheck style={{ fontSize: '1.5rem', color: '#000' }} />;
    if (finalMode === 'phone') return <LuPhone style={{ fontSize: '1.5rem', color: '#000' }} />;
    if (finalMode === 'password') return <LuLock style={{ fontSize: '1.5rem', color: '#000' }} />;
  }

  if (finalMode === 'email') return <LuMail style={{ fontSize: '1.5rem', color: '#000' }} />;
  if (finalMode === 'phone') return <LuPhone style={{ fontSize: '1.5rem', color: '#000' }} />;
  if (finalMode === 'password') return <LuLock style={{ fontSize: '1.5rem', color: '#000' }} />;

  return <LuCircleCheck style={{ fontSize: '1.5rem', color: '#000' }} />;
}

// Progress text
function ProgressPill({ step, total }) {
  const currentStep = Math.max(1, Math.min(step || 1, total || 1));
  const totalSteps = Math.max(total || 1, 1);

  return (
    <div
      style={{
        color: '#0b0b0b',
        fontSize: '0.9rem',
        marginRight: 10,
        fontWeight: 750,
        lineHeight: 1,
        letterSpacing: '-0.01em',
        whiteSpace: 'nowrap',
        fontFamily: "'Inter', system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
      }}
    >
      {currentStep} of {totalSteps}
    </div>
  );
}

// -------------------------------
// MAIN COMPONENT
// -------------------------------
export default function UpdateAccountPage({ userId, mode }) {
  const navigate = useNavigate();
  const { mode: routeMode } = useParams();
  const [searchParams] = useSearchParams();
  const queryUserId = searchParams.get('userId');

  const finalUserId = userId || queryUserId;
  const finalMode = mode || routeMode;

  const [step, setStep] = useState(1);

  // For phone/password flows
  const [currentEmail, setCurrentEmail] = useState('');

  // Email flow
  const [newEmailForStep1, setNewEmailForStep1] = useState('');

  // 3-digit code
  const [codeDigits, setCodeDigits] = useState(['', '', '']);
  const [shakeCode, setShakeCode] = useState(false);
  const [codeError, setCodeError] = useState(false);

  // Step 3
  const [currentPassword, setCurrentPassword] = useState('');
  const [newValue, setNewValue] = useState('');
  const [confirmValue, setConfirmValue] = useState('');

  const [shakePasswords, setShakePasswords] = useState(false);
  const [passwordError, setPasswordError] = useState(false);
  const [confirmError, setConfirmError] = useState(false);

  const { addToast } = useToaster();

  useEffect(() => {
    if (finalMode !== 'email' && finalUserId) {
      axios
        .get(`${process.env.REACT_APP_API_URL}/users/${finalUserId}`)
        .then((res) => {
          if (res.data.email) setCurrentEmail(res.data.email);
        })
        .catch((err) => {
          console.error('Failed to fetch user =>', err);
        });
    }
  }, [finalUserId, finalMode]);

  function handleNavBack() {
    navigate('/settings');
  }

  function handleStepBack() {
    setStep(step - 1);
  }

  // -------------------------------
  // CODE INPUTS
  // -------------------------------
  function handleCodeChange(event, idx) {
  if (idx > 0 && !codeDigits[idx - 1]) {
    document.getElementById(`update-code-${idx - 1}`)?.focus();
    return;
  }

  const value = event.target.value.replace(/\D/g, '').slice(-1);
  const nextDigits = [...codeDigits];

  nextDigits[idx] = value;

  if (!value) {
    for (let i = idx + 1; i < nextDigits.length; i += 1) {
      nextDigits[i] = '';
    }
  }

  setCodeDigits(nextDigits);

  if (value && idx < nextDigits.length - 1) {
    requestAnimationFrame(() => {
      document.getElementById(`update-code-${idx + 1}`)?.focus();
    });
  }
}

function handleCodeKeyDown(event, idx) {
  if (event.key === 'Backspace' && !codeDigits[idx] && idx > 0) {
    event.preventDefault();

    const nextDigits = [...codeDigits];
    nextDigits[idx - 1] = '';

    for (let i = idx; i < nextDigits.length; i += 1) {
      nextDigits[i] = '';
    }

    setCodeDigits(nextDigits);

    requestAnimationFrame(() => {
      document.getElementById(`update-code-${idx - 1}`)?.focus();
    });
  }
}

  // -------------------------------
  // STEP 1: SEND CODE
  // -------------------------------
  async function handleSendCode() {
    if (!finalUserId) {
      addToast('No userId found in props or query.', 'error');
      return;
    }

    try {
      if (finalMode === 'email') {
        if (!newEmailForStep1.trim()) {
          addToast('Please enter a new email.', 'error');
          return;
        }

        await axios.post(
          `${process.env.REACT_APP_API_URL}/users/${finalUserId}/sendUpdateEmailCode`,
          { newEmail: newEmailForStep1 }
        );
      } else if (finalMode === 'phone') {
        await axios.post(
          `${process.env.REACT_APP_API_URL}/users/${finalUserId}/sendUpdatePhoneCode`
        );
      } else if (finalMode === 'password') {
        await axios.post(
          `${process.env.REACT_APP_API_URL}/users/${finalUserId}/sendUpdatePasswordCode`
        );
      }

      setStep(2);
    } catch (err) {
      addToast(err.response?.data?.message || err.message, 'error');
    }
  }

  // -------------------------------
  // STEP 2: VERIFY CODE
  // -------------------------------
  async function handleCheckCode() {
    const codeStr = codeDigits.join('');

    if (!codeStr.trim() || codeStr.length < 3) {
      return;
    }

    setCodeError(false);

    if (!finalUserId) {
      addToast('No userId found in props or query.', 'error');
      return;
    }

    try {
      if (finalMode === 'email') {
        await axios.post(
          `${process.env.REACT_APP_API_URL}/users/${finalUserId}/checkUpdateEmailCode`,
          { code: codeStr }
        );
      } else if (finalMode === 'phone') {
        await axios.post(
          `${process.env.REACT_APP_API_URL}/users/${finalUserId}/checkUpdatePhoneCode`,
          { code: codeStr }
        );
      } else if (finalMode === 'password') {
        await axios.post(
          `${process.env.REACT_APP_API_URL}/users/${finalUserId}/checkUpdatePasswordCode`,
          { code: codeStr }
        );
      }

      setStep(3);
    } catch (err) {
      addToast(err.response?.data?.message || 'Invalid code', 'error');
      setCodeError(true);
      setShakeCode(true);

      setTimeout(() => {
        setCodeDigits(['', '', '']);
        setShakeCode(false);
        setCodeError(false);
      }, 300);
    }
  }

  async function handleResendCode() {
    try {
      await handleSendCode();
      addToast('Verification code resent successfully!', 'success');
    } catch {
      // handled in handleSendCode
    }
  }

  // -------------------------------
  // STEP 3: FINALIZE
  // -------------------------------
  async function handleFinalize() {
    if (!finalUserId) {
      addToast('No userId found in props or query.', 'error');
      return;
    }

    if (finalMode === 'email') {
      if (!currentPassword.trim()) {
        addToast('Please enter your current password.', 'error');
        return;
      }

      try {
        await axios.post(
          `${process.env.REACT_APP_API_URL}/users/${finalUserId}/finalizeUpdateEmail`,
          { currentPassword }
        );

        navigate('/settings', { state: { updatedType: 'email' } });
      } catch (err) {
        addToast(err.response?.data?.message || err.message, 'error');
      }

      return;
    }

    if (!newValue.trim() || !confirmValue.trim()) {
      addToast('Please fill out both fields.', 'error');
      return;
    }

    if (newValue !== confirmValue) {
      addToast('The two fields do not match.', 'error');
      setPasswordError(true);
      setConfirmError(true);
      setShakePasswords(true);
      setNewValue('');
      setConfirmValue('');

      setTimeout(() => {
        setShakePasswords(false);
        setPasswordError(false);
        setConfirmError(false);
      }, 300);

      return;
    }

    try {
      if (finalMode === 'phone') {
        await axios.post(
          `${process.env.REACT_APP_API_URL}/users/${finalUserId}/finalizeUpdatePhone`,
          {
            newPhone: newValue,
            confirmPhone: confirmValue,
          }
        );

        navigate('/settings', { state: { updatedType: 'phone' } });
      } else {
        await axios.post(
          `${process.env.REACT_APP_API_URL}/users/${finalUserId}/finalizeUpdatePassword`,
          {
            newPassword: newValue,
            confirmPassword: confirmValue,
          }
        );

        navigate('/settings', { state: { updatedType: 'password' } });
      }
    } catch (err) {
      addToast(err.response?.data?.message || err.message, 'error');
    }
  }

  // -------------------------------
  // ACTIVE STATE
  // -------------------------------
  const emailStep1Filled = finalMode !== 'email' || !!newEmailForStep1.trim();
  const codeFilled = codeDigits.every((digit) => digit.trim() !== '');
  const finalizeFilled =
    finalMode === 'email'
      ? !!currentPassword.trim()
      : !!newValue.trim() && !!confirmValue.trim();

  // -------------------------------
  // STEP CONTENT
  // -------------------------------
  function renderStepContent() {
    switch (step) {
      case 1: {
        const icon = getStepIcon(step, finalMode);

        if (finalMode === 'email') {
          return (
            <div>
              <style>{shakeKeyframes}</style>

              <div style={headingCircleContainer}>{icon}</div>

              <h2 style={headingStyle}>Enter New Email</h2>

              <p style={subTitleStyle}>
                We’ll send a code to verify it
              </p>

              <div
                style={{
                  textAlign: 'left',
                  maxWidth: 300,
                  margin: '0 auto 1.25rem',
                }}
              >
                <label style={labelStyle}>
                  New Email
                </label>

                <input
                  type="email"
                  placeholder="example@email.com"
                  style={getInputStyle(false)}
                  value={newEmailForStep1}
                  onChange={(event) => {
                    setNewEmailForStep1(event.target.value);
                  }}
                />
              </div>

              <button
                type="button"
                onClick={handleSendCode}
                disabled={!emailStep1Filled}
                style={getPrimaryButtonStyle(emailStep1Filled)}
              >
                Continue
              </button>
            </div>
          );
        }

        if (finalMode === 'phone') {
          return (
            <div>
              <style>{shakeKeyframes}</style>

              <div style={headingCircleContainer}>{icon}</div>

              <h2 style={headingStyle}>
                Update Phone
              </h2>

              <p style={subTitleStyle}>
                We’ll send a verification code to your email
                <br />

                <span style={highlightStyle}>
                  {currentEmail || '…'}
                </span>
              </p>

              <button
                type="button"
                onClick={handleSendCode}
                style={getPrimaryButtonStyle(true)}
              >
                Continue
              </button>
            </div>
          );
        }

        if (finalMode === 'password') {
          return (
            <div>
              <style>{shakeKeyframes}</style>

              <div style={headingCircleContainer}>{icon}</div>

              <h2 style={headingStyle}>
                Update Password
              </h2>

              <p style={subTitleStyle}>
                We’ll send a verification code to your email
                <br />

                <span style={highlightStyle}>
                  {currentEmail || '…'}
                </span>
              </p>

              <button
                type="button"
                onClick={handleSendCode}
                style={getPrimaryButtonStyle(true)}
              >
                Continue
              </button>
            </div>
          );
        }

        break;
      }

      case 2: {
        const icon = getStepIcon(step, finalMode);

        return (
          <div
            style={{
              textAlign: 'center',
              maxWidth: 460,
              margin: '0 auto',
            }}
          >
            <style>{shakeKeyframes}</style>

            <div style={headingCircleContainer}>{icon}</div>

            <h2 style={headingStyle}>
              Verification Code
            </h2>

            <p style={subTitleStyle}>
              Enter the 3-digit code we sent to
              <br />

              <span style={highlightStyle}>
                {finalMode === 'email'
                  ? newEmailForStep1
                  : currentEmail || '…'}
              </span>
            </p>

            <div
              className={shakeCode ? 'shake' : ''}
              style={{
                display: 'flex',
                justifyContent: 'center',
                gap: 10,
                marginBottom: '1.25rem',
              }}
            >
              {codeDigits.map((digit, idx) => {
  const previousIncomplete = idx > 0 && !codeDigits[idx - 1];

  return (
    <input
      key={idx}
      id={`update-code-${idx}`}
      className="update-code-input"
      type="text"
      inputMode="numeric"
      pattern="[0-9]*"
      autoComplete={idx === 0 ? 'one-time-code' : 'off'}
      maxLength={1}
      placeholder="0"
      value={digit}
      onFocus={(event) => {
        if (previousIncomplete) {
          event.preventDefault();
          document.getElementById(`update-code-${idx - 1}`)?.focus();
        }
      }}
      onMouseDown={(event) => {
        if (previousIncomplete) {
          event.preventDefault();
          document.getElementById(`update-code-${idx - 1}`)?.focus();
        }
      }}
      onChange={(event) => handleCodeChange(event, idx)}
      onKeyDown={(event) => handleCodeKeyDown(event, idx)}
      style={{
        width: 52,
        height: 52,
        fontSize: '1.35rem',
        textAlign: 'center',
        border: `1.5px solid ${codeError ? '#EF4444' : digit ? '#d8d8d8' : '#e5e7eb'}`,
        borderRadius: 10,
        boxSizing: 'border-box',
        background: '#fff',
        color: '#0b0b0b',
        outline: 'none',
        fontWeight: 650,
        letterSpacing: '-0.02em',
        cursor: 'text',
        fontFamily: "'Inter', system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
      }}
    />
  );
})}
            </div>

            <p
              style={{
                fontSize: '0.9rem',
                color: '#6b7280',
                marginTop: '0.75rem',
                marginBottom: '1.75rem',
              }}
            >
              Didn’t get the code?{' '}

              <span
                onClick={handleResendCode}
                style={{
                  cursor: 'pointer',
                  color: '#000',
                  fontWeight: 800,
                }}
              >
                Click to resend
              </span>
            </p>

            <button
              type="button"
              onClick={handleCheckCode}
              disabled={!codeFilled}
              style={getPrimaryButtonStyle(codeFilled)}
            >
              Continue
            </button>

            <button
              type="button"
              onClick={handleStepBack}
              style={backBelowButtonStyle}
            >
              Back
            </button>
          </div>
        );
      }

      case 3: {
        const icon = getStepIcon(step, finalMode);

        if (finalMode === 'email') {
          return (
            <div
              style={{
                textAlign: 'center',
                maxWidth: 460,
                margin: '0 auto',
              }}
            >
              <style>{shakeKeyframes}</style>

              <div style={headingCircleContainer}>{icon}</div>

              <h2 style={headingStyle}>
                Verify Ownership
              </h2>

              <p style={subTitleStyle}>
                Enter your <strong>current password</strong> to confirm.
              </p>

              <div
                style={{
                  textAlign: 'left',
                  maxWidth: 300,
                  margin: '0 auto 1rem',
                }}
              >
                <label style={labelStyle}>
                  Current Password
                </label>

                <input
                  type="password"
                  placeholder="••••••"
                  style={getInputStyle(false)}
                  value={currentPassword}
                  onChange={(event) => {
                    setCurrentPassword(event.target.value);
                  }}
                />
              </div>

              <button
                type="button"
                onClick={handleFinalize}
                disabled={!currentPassword.trim()}
                style={getPrimaryButtonStyle(!!currentPassword.trim())}
              >
                Submit
              </button>

              <button
                type="button"
                onClick={handleStepBack}
                style={backBelowButtonStyle}
              >
                Back
              </button>
            </div>
          );
        }

        if (finalMode === 'phone') {
          return (
            <div
              style={{
                textAlign: 'center',
                maxWidth: 460,
                margin: '0 auto',
              }}
            >
              <style>{shakeKeyframes}</style>

              <div style={headingCircleContainer}>{icon}</div>

              <h2 style={headingStyle}>
                Update Phone
              </h2>

              <p style={subTitleStyle}>
                Enter your new phone number.
              </p>

              <div
                className={shakePasswords ? 'shake' : ''}
                style={{
                  textAlign: 'left',
                  maxWidth: 300,
                  margin: '0 auto 1rem',
                }}
              >
                <label style={labelStyle}>
                  New Phone
                </label>

                <input
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  placeholder="+1 (555) 123-4567"
                  style={getInputStyle(passwordError)}
                  value={newValue}
                  onChange={(event) => {
                    setNewValue(event.target.value);
                  }}
                />
              </div>

              <div
                className={shakePasswords ? 'shake' : ''}
                style={{
                  textAlign: 'left',
                  maxWidth: 300,
                  margin: '0 auto 1.25rem',
                }}
              >
                <label style={labelStyle}>
                  Confirm Phone
                </label>

                <input
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  placeholder="+1 (555) 123-4567"
                  style={getInputStyle(confirmError)}
                  value={confirmValue}
                  onChange={(event) => {
                    setConfirmValue(event.target.value);
                  }}
                />
              </div>

              <button
                type="button"
                onClick={handleFinalize}
                disabled={!finalizeFilled}
                style={getPrimaryButtonStyle(finalizeFilled)}
              >
                Submit
              </button>

              <button
                type="button"
                onClick={handleStepBack}
                style={backBelowButtonStyle}
              >
                Back
              </button>
            </div>
          );
        }

        if (finalMode === 'password') {
          return (
            <div
              style={{
                textAlign: 'center',
                maxWidth: 460,
                margin: '0 auto',
              }}
            >
              <style>{shakeKeyframes}</style>

              <div style={headingCircleContainer}>{icon}</div>

              <h2 style={headingStyle}>
                Update Password
              </h2>

              <p style={subTitleStyle}>
                Enter and confirm your new password.
              </p>

              <div
                className={shakePasswords ? 'shake' : ''}
                style={{
                  textAlign: 'left',
                  maxWidth: 300,
                  margin: '0 auto 1rem',
                }}
              >
                <label style={labelStyle}>
                  New Password
                </label>

                <input
                  type="password"
                  placeholder="••••••"
                  style={getInputStyle(passwordError)}
                  value={newValue}
                  onChange={(event) => {
                    setNewValue(event.target.value);
                  }}
                />
              </div>

              <div
                className={shakePasswords ? 'shake' : ''}
                style={{
                  textAlign: 'left',
                  maxWidth: 300,
                  margin: '0 auto 1.25rem',
                }}
              >
                <label style={labelStyle}>
                  Confirm Password
                </label>

                <input
                  type="password"
                  placeholder="••••••"
                  style={getInputStyle(confirmError)}
                  value={confirmValue}
                  onChange={(event) => {
                    setConfirmValue(event.target.value);
                  }}
                />
              </div>

              <button
                type="button"
                onClick={handleFinalize}
                disabled={!finalizeFilled}
                style={getPrimaryButtonStyle(finalizeFilled)}
              >
                Submit
              </button>

              <button
                type="button"
                onClick={handleStepBack}
                style={backBelowButtonStyle}
              >
                Back
              </button>
            </div>
          );
        }

        break;
      }

      default:
        return <div>Unknown step</div>;
    }
  }

  return (
    <div style={pageStyle}>
      {step >= 1 && step < 4 && (
        <header style={headerContainerStyle}>
          <div style={headerLogoRowStyle}>
            <TribeLogo style={logoSvgStyle} />
          </div>

          <div style={headerNavRowStyle}>
            <button
              type="button"
              onClick={handleNavBack}
              style={previousBackButtonStyle}
              aria-label="Back to settings"
            >
              <span style={previousBackButtonFaceStyle} aria-hidden="true">
                <LuArrowLeft size={16} color="#fff" />
              </span>
            </button>

            <div style={pageTitlePlainStyle}>
              {getNavbarTitle()}
            </div>

            <div style={progressContainerStyle}>
              <ProgressPill step={step} total={3} />
            </div>
          </div>
        </header>
      )}

      <div style={containerStyle}>
        {renderStepContent()}
      </div>
    </div>
  );
}
